import apiClient from "../utils/axios";

export interface Invoice {
  id: string;
  number?: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  amount_remaining: number;
  currency: string;
  created: number;
  due_date?: number;
  next_payment_attempt?: number;
  description?: string;
  invoice_pdf?: string;
  is_pending_summary?: boolean;
  pending_setup_fees?: number;
  lines?: {
    data: InvoiceLineItem[];
  };
}

export interface InvoiceLineItem {
  id: string;
  amount: number;
  currency: string;
  description?: string;
  quantity?: number;
  price?: {
    id: string;
    unit_amount: number;
    currency: string;
    recurring?: {
      interval: string;
      interval_count: number;
    };
    product?: {
      name: string;
      description?: string;
    };
  };
  period?: {
    start: number;
    end: number;
  };
  type?: string;
  metadata?: Record<string, string>;
}

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  description?: string;
  receipt_url?: string;
  payment_method_details?: {
    brand?: string;
    last4?: string;
  };
}

export interface UpcomingInvoicesResponse {
  upcoming_invoices: Invoice[];
  pending_items: any[];
  message: string;
}

export class InvoiceService {
  
  static async getOrganizationInvoices(
    organizationId: string,
    token: string
  ): Promise<Invoice[]> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/invoices`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data || [];
  }

  static async getOrganizationPayments(
    organizationId: string,
    token: string
  ): Promise<Payment[]> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/payments`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data || [];
  }

  static async getUpcomingInvoices(organizationId: string, token: string): Promise<UpcomingInvoicesResponse> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/upcoming-invoices`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static async getInvoiceDownloadUrl(
    organizationId: string,
    invoiceId: string,
    token: string
  ): Promise<{ download_url: string }> {
    const response = await apiClient.get(
      `/api/organization/${organizationId}/stripe/invoice/${invoiceId}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  static formatCurrency(amount: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Stripe amounts are in cents
  }

  static formatDate(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(timestamp: number): string {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'succeeded':
        return 'green';
      case 'open':
      case 'draft':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'failed':
        return 'red';
      case 'void':
      case 'canceled':
        return 'default';
      default:
        return 'default';
    }
  }

  static getUpcomingStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'purple';
      case 'open':
        return 'blue';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  }

  static getUpcomingStatusText(invoice: Invoice): string {
    if (invoice.is_pending_summary) {
      return invoice.pending_setup_fees && invoice.pending_setup_fees > 0 
        ? 'SETUP FEES PENDING' 
        : 'CHARGES PENDING';
    }
    
    return invoice.status.toUpperCase();
  }

  static getUpcomingDescription(invoice: Invoice): string {
    if (invoice.is_pending_summary) {
      if (invoice.pending_setup_fees && invoice.pending_setup_fees > 0) {
        return `${invoice.pending_setup_fees} setup fee${invoice.pending_setup_fees > 1 ? 's' : ''} to be charged when trial ends`;
      }
      return 'Pending charges to be processed';
    }
    
    // NOTE: this isn't showing correctly when it is a monthly subscription
    // // For upcoming invoices, show when they're for
    // const billingDate = invoice.next_payment_attempt || invoice.due_date;
    // if (billingDate) {
    //   const year = new Date(billingDate * 1000).getFullYear();
    //   return `Annual subscription renewal for ${year}`;
    // }
    
    return invoice.description || 'Next billing cycle charge';
  }

  static formatLineItemDescription(lineItem: InvoiceLineItem): string {
    // Try to get description from various sources
    if (lineItem.description) {
      return lineItem.description;
    }
    
    if (lineItem.price?.product?.name) {
      return lineItem.price.product.name;
    }
    
    if (lineItem.price?.product?.description) {
      return lineItem.price.product.description;
    }
    
    // Check metadata for setup fees
    if (lineItem.metadata?.type === 'setup_fee') {
      const facilityCount = lineItem.quantity || 1;
      return `Setup fee for ${facilityCount} facilit${facilityCount > 1 ? 'ies' : 'y'}`;
    }
    
    // For recurring charges
    if (lineItem.price?.recurring) {
      const interval = lineItem.price.recurring.interval;
      const quantity = lineItem.quantity || 1;
      return `${quantity} facilit${quantity > 1 ? 'ies' : 'y'} - ${interval}ly subscription`;
    }
    
    return 'Billing charge';
  }

  static formatLineItemPeriod(lineItem: InvoiceLineItem): string | null {
    if (!lineItem.period) {
      return null;
    }
    
    const start = this.formatDate(lineItem.period.start);
    const end = this.formatDate(lineItem.period.end);
    return `${start} - ${end}`;
  }

  static getLineItemType(lineItem: InvoiceLineItem): string {
    if (lineItem.metadata?.type === 'setup_fee') {
      return 'Setup Fee';
    }
    
    if (lineItem.price?.recurring) {
      return 'Subscription';
    }
    
    return 'Charge';
  }

  static hasLineItemDetails(invoice: Invoice): boolean {
    return !!(invoice.lines && invoice.lines.data && invoice.lines.data.length > 0);
  }
} 